import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { CheckCircle, ShieldCheck, Hourglass, Warning } from '@phosphor-icons/react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SLATE = '#0B1426';
const CORAL = '#FF4E6A';
const MUTED = '#5B6577';
const RULE  = 'rgba(11,20,38,0.10)';
const IVORY = '#F5F1E8';

const TONE = {
  submitted:                     { icon: Hourglass,   color: MUTED, bg: 'rgba(11,20,38,0.05)' },
  documents_needed:              { icon: Warning,     color: '#A07A14', bg: 'rgba(212,160,23,0.10)' },
  scientific_review_in_progress: { icon: Hourglass,   color: CORAL, bg: 'rgba(255,78,106,0.06)' },
  hale_review_pending:           { icon: Hourglass,   color: CORAL, bg: 'rgba(255,78,106,0.06)' },
  approved_by_hale:              { icon: CheckCircle, color: '#16624B', bg: 'rgba(22,98,75,0.10)' },
  not_approved:                  { icon: Warning,     color: '#B0312D', bg: 'rgba(176,49,45,0.08)' },
  badge_issued:                  { icon: ShieldCheck, color: '#16624B', bg: 'rgba(22,98,75,0.10)' },
};

/* Compact brand-facing workflow status card.
 *
 * Surfaces the sanitised brand-facing view from
 * GET /api/hale-review/my-submission. NEVER renders internal AG notes —
 * the API itself strips them before the brand ever sees the response. */
export default function BrandWorkflowStatusCard() {
  const [sub, setSub] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API_URL}/api/hale-review/my-submission`, { withCredentials: true });
        if (!cancelled) setSub(r.data?.submission || null);
      } catch (_e) {
        // best-effort — no submission yet is fine
      } finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, []);

  if (loading) {
    return (
      <div className="rounded-sm border p-7" style={{ borderColor: RULE, backgroundColor: 'rgba(255,255,255,0.5)' }}
           data-testid="hale-brand-status-loading">
        <p className="text-[10px] font-mono uppercase tracking-[0.3em]" style={{ color: MUTED }}>Hale review status</p>
        <p className="mt-3 text-sm" style={{ color: MUTED }}>Loading…</p>
      </div>
    );
  }
  if (!sub) {
    return (
      <div className="rounded-sm border p-7" style={{ borderColor: RULE, backgroundColor: 'rgba(255,255,255,0.5)' }}
           data-testid="hale-brand-status-empty">
        <p className="text-[10px] font-mono uppercase tracking-[0.3em]" style={{ color: CORAL }}>Hale review status</p>
        <p className="mt-3 text-sm" style={{ color: SLATE }}>You haven&apos;t submitted to Hale yet.</p>
        <p className="mt-1 text-[12px]" style={{ color: MUTED }}>
          Start an application from <a href="/hale/apply" className="underline">/hale/apply</a> to begin scientific review.
        </p>
      </div>
    );
  }

  const tone = TONE[sub.brand_status] || TONE.submitted;
  const Icon = tone.icon;

  return (
    <div
      className="rounded-sm border p-7"
      style={{ borderColor: RULE, backgroundColor: tone.bg }}
      data-testid="hale-brand-status-card"
    >
      <p className="text-[10px] font-mono uppercase tracking-[0.3em]" style={{ color: CORAL }}>
        Hale review status
      </p>
      <div className="mt-3 flex items-center gap-3">
        <Icon size={28} weight="fill" style={{ color: tone.color }} />
        <div>
          <p className="text-xl font-extrabold tracking-tight"
             style={{ fontFamily: '"Cormorant Garamond", serif', color: SLATE }}
             data-testid="hale-brand-status-label">
            {sub.brand_status_label}
          </p>
          <p className="text-[12px] font-mono uppercase tracking-wider" style={{ color: tone.color }}>
            {sub.brand_name || 'Your submission'}
          </p>
        </div>
      </div>

      {sub.released_findings && (
        <div className="mt-5 rounded-sm border p-4" style={{ borderColor: RULE, backgroundColor: IVORY }}
             data-testid="hale-brand-released-findings">
          <p className="text-[10px] font-mono uppercase tracking-[0.25em] mb-2" style={{ color: MUTED }}>
            Audit findings · released by AG
          </p>
          <p className="text-[13px] leading-relaxed whitespace-pre-wrap" style={{ color: SLATE }}>
            {sub.released_findings}
          </p>
        </div>
      )}

      {sub.badge?.issued_at && (
        <div className="mt-5 rounded-sm border p-4 flex items-center gap-3"
             style={{ borderColor: RULE, backgroundColor: IVORY }}
             data-testid="hale-brand-badge-card">
          <ShieldCheck size={24} weight="fill" style={{ color: CORAL }} />
          <div>
            <p className="text-sm font-semibold" style={{ color: SLATE }}>{sub.badge.label}</p>
            <p className="text-[11px] font-mono uppercase tracking-wider" style={{ color: MUTED }}>
              Issued {new Date(sub.badge.issued_at).toLocaleDateString()} ·{' '}
              <a className="underline" href={`${API_URL}${sub.badge.embed_url}`} target="_blank" rel="noreferrer" data-testid="hale-brand-badge-embed-link">
                View embeddable badge
              </a>
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
